<template>
    <div class="left-menu">
        <!-- Logo Header with Close Button -->
        <div class="logo-header" :class="logoHeaderClass">
            <img v-if="!isMini" src="@/assets/logo-white.png" alt="Logo GLTN" class="logo-image" />
            <img v-else src="@/assets/logo-white-small.svg" alt="Logo GLTN" class="logo-image" />
            <q-btn flat dense round icon="close" color="white" size="sm" @click="$emit('toggle-drawer')"
                class="close-btn-mobile">
                <q-tooltip>Cerrar menú</q-tooltip>
            </q-btn>
        </div>

        <q-separator class="q-my-sm" />

        <!-- User Profile Section -->
        <Link href="/perfil" v-if="!isMini" class="user-profile user-profile-link">
            <q-avatar size="48px" class="user-avatar">
                <img :src="`https://api.dicebear.com/9.x/identicon/svg?seed=${user?.name}`" alt="Avatar" />
            </q-avatar>
            <div class="user-info">
                <div class="user-name">{{ user?.name }}</div>
                <div class="user-role">{{ user?.email }}</div>
            </div>
            <q-icon name="chevron_right" size="18px" color="grey-5" class="profile-arrow" />
        </Link>

        <!-- Mini User Avatar -->
        <Link href="/perfil" v-else class="user-avatar-mini user-profile-link">
            <q-avatar size="40px" class="user-avatar">
                <img :src="`https://api.dicebear.com/9.x/identicon/svg?seed=${user?.name}`" alt="Avatar" />
            </q-avatar>
            <q-tooltip anchor="center right" self="center left" :offset="[10, 0]">Mi Perfil</q-tooltip>
        </Link>

        <q-separator class="q-my-md" />

        <!-- Menu Items -->
        <q-list class="menu-list">
            <template v-for="menu in menuItems" :key="menu.title">
                <q-item clickable v-ripple :href="menu.link" tag="Link" :active="isActive(menu.link)"
                    active-class="menu-item-active" class="menu-item">
                    <q-item-section avatar>
                        <q-avatar :class="['menu-avatar', { 'menu-avatar-active': isActive(menu.link) }]" size="40px">
                            <q-icon :name="menu.icon" size="24px" />
                        </q-avatar>
                    </q-item-section>
                    <q-item-section v-if="!isMini">
                        <q-item-label class="menu-label">{{ menu.title }}</q-item-label>
                        <q-item-label caption class="menu-caption">
                            {{ menu.caption }}
                        </q-item-label>
                    </q-item-section>
                    <q-item-section side v-if="isActive(menu.link) && !isMini">
                        <q-icon name="chevron_right" size="20px" color="primary" />
                    </q-item-section>

                    <!-- Tooltip when mini -->
                    <q-tooltip v-if="isMini" anchor="center right" self="center left" :offset="[10, 0]">
                        <div class="text-subtitle2">{{ menu.title }}</div>
                        <div class="text-caption">{{ menu.caption }}</div>
                    </q-tooltip>
                </q-item>
            </template>
        </q-list>

        <!-- Menu Footer -->
        <div class="menu-footer">
            <q-separator class="q-mb-md" />

            <!-- Logout Button -->
            <q-btn unelevated color="negative" :icon="isMini ? 'logout' : undefined"
                :label="isMini ? undefined : 'Cerrar Sesión'" class="logout-btn full-width" :loading="loginOut"
                @click="$emit('logout')" no-caps>
                <q-tooltip v-if="isMini">Cerrar Sesión</q-tooltip>
            </q-btn>

            <!-- Toggle Mini Button -->
            <div class="toggle-mini-container q-mt-md">
                <q-btn flat dense :icon="isMini ? 'chevron_right' : 'chevron_left'" color="primary"
                    @click="$emit('toggle-mini')" class="toggle-mini-btn full-width" no-caps>
                    <span v-if="!isMini" class="q-ml-xs">Contraer menú</span>
                    <q-tooltip v-if="isMini">Expandir</q-tooltip>
                </q-btn>
            </div>
        </div>
    </div>
</template>

<script setup>
import { Link, usePage } from "@inertiajs/vue3";
import { route } from "ziggy-js";
import { computed } from "vue";

const props = defineProps({
    user: Object,
    loginOut: Boolean,
    isMini: {
        type: Boolean,
        default: false
    }
});

defineEmits(['logout', 'toggle-drawer', 'toggle-mini']);

const page = usePage();

const menuList = [
    { title: "Inicio", icon: "home", link: "/", slug: "inicio", caption: "Dashboard principal" },
    { title: "Productos", icon: "inventory_2", link: "/productos", slug: "productos", caption: "Catálogo" },
    { title: "Presupuestos", icon: "assignment", link: "/presupuestos", slug: "presupuestos", caption: "Cotizaciones" },
    { title: "Obras", icon: "engineering", link: "/obras-grid", slug: "obras", caption: "Gestión de proyectos" },
    { title: "Auto Agendamiento", icon: "event_available", link: route("autoagendamiento.index"), slug: "autoagendamiento", caption: "Agendar visitas" },
    // { title: "Ingenierías", icon: "construction", link: "/ingenierias", slug: "ingenierias", caption: "Ingenería de cámaras" },
    { title: "Planificación", icon: "event_note", link: "/planificacion", slug: "planificacion", caption: "Planificación de proyectos" },
    { title: "Decodificador", icon: "qr_code_scanner", link: "/decodificador", slug: "decodificador", caption: "Codificación de productos" },
    { title: "Imágenes", icon: "photo_library", link: "/imagenes", slug: "imagenes", caption: "Gestión de imágenes" },
    { title: "Configuración", icon: "settings", link: "/config", slug: "configuracion", caption: "Ajustes del sistema" },
    { title: "Roles", icon: "admin_panel_settings", link: "/roles", slug: "roles", caption: "Permisos y roles" },
    { title: "Usuarios", icon: "people", link: "/usuarios", slug: "usuarios", caption: "Gestión de usuarios" },
];

const menuItems = computed(() => {
    return menuList.map(item => {
        return page.props?.auth?.can.includes('menu.' + item.slug) ? item : null;
    }).filter(Boolean);
});

const logoHeaderClass = computed(() => {
    const env = page.props?.appEnv;
    if (env === 'local') return 'bg-local';
    if (env === 'val' || env === 'validation') return 'bg-val';
    return 'bg-prod'; // production y cualquier otro entorno
});

const isActive = (link) => {
    const currentPath = page.url;
    // Exact match for home
    if (link === "/" && currentPath === "/") return true;
    // Partial match for other routes
    if (link !== "/" && currentPath.startsWith(link)) return true;
    return false;
};


</script>

<style lang="scss" scoped>
.left-menu {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 0 0 20px 0;
    background: linear-gradient(180deg, #fafafa 0%, #f5f5f5 100%);
}

// Logo Header
.logo-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    animation: fadeIn 0.5s ease-out;
    gap: 12px;
    height: 80px;
    transition: all 0.3s ease;

    // Production - Azul (default)
    &.bg-prod {
        background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
        box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3);
    }

    // Local - Negro
    &.bg-local {
        background: linear-gradient(135deg, #212121 0%, #424242 100%);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
    }

    // Validation - Rojo
    &.bg-val {
        background: linear-gradient(135deg, #d32f2f 0%, #c62828 100%);
        box-shadow: 0 4px 12px rgba(211, 47, 47, 0.3);
    }

    .logo-image {
        max-height: 45px;
        width: auto;
        flex: 1;
        transition: all 0.3s ease;
    }

    .logo-icon {
        transition: all 0.3s ease;
    }

    .close-btn-mobile {
        opacity: 0.9;
        transition: all 0.3s ease;

        &:hover {
            opacity: 1;
            transform: scale(1.1);
        }

        // Hide on desktop (>= 1024px)
        @media (min-width: 1024px) {
            display: none;
        }
    }
}

// User Profile Link wrapper
.user-profile-link {
    display: flex;
    text-decoration: none;
    color: inherit;
    cursor: pointer;
}

// User Profile
.user-profile {
    align-items: center;
    gap: 12px;
    padding: 16px 20px;
    margin: 0 16px;
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
    animation: slideInDown 0.5s ease-out;

    &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
        transform: translateY(-2px);

        .profile-arrow {
            opacity: 1;
            transform: translateX(2px);
        }
    }

    .user-avatar {
        box-shadow: 0 2px 8px rgba(25, 118, 210, 0.2);
    }

    .user-info {
        flex: 1;
        min-width: 0;

        .user-name {
            font-size: 15px;
            font-weight: 600;
            color: #424242;
            margin-bottom: 2px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        .user-role {
            font-size: 12px;
            color: #9e9e9e;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }

    .profile-arrow {
        opacity: 0;
        transition: all 0.3s ease;
        flex-shrink: 0;
    }
}

// Mini User Avatar
.user-avatar-mini {
    display: flex;
    justify-content: center;
    padding: 12px;
    animation: fadeIn 0.3s ease-out;

    .user-avatar {
        box-shadow: 0 2px 8px rgba(25, 118, 210, 0.2);
        transition: all 0.3s ease;

        &:hover {
            transform: scale(1.1);
            box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3);
        }
    }
}

// Menu List
.menu-list {
    flex: 1;
    padding: 0 8px;
    overflow-y: auto;

    // Custom scrollbar
    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: #e0e0e0;
        border-radius: 3px;

        &:hover {
            background: #bdbdbd;
        }
    }
}

// Menu Items
.menu-item {
    margin-bottom: 4px;
    border-radius: 10px;
    transition: all 0.3s ease;
    min-height: 64px;

    &:hover {
        background: linear-gradient(to right, #f5f9ff, #e3f2fd);
        transform: translateX(4px);
        box-shadow: 0 2px 8px rgba(25, 118, 210, 0.1);
    }

    // Active state
    &.menu-item-active {
        background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
        box-shadow: 0 2px 8px rgba(25, 118, 210, 0.2);
        border-left: 4px solid #1976d2;

        .menu-label {
            color: #1976d2;
            font-weight: 600;
        }

        .menu-caption {
            color: #1565c0;
        }
    }
}

// Menu Avatar
.menu-avatar {
    background: linear-gradient(135deg, #f5f5f5 0%, #eeeeee 100%);
    color: #616161;
    transition: all 0.3s ease;

    &.menu-avatar-active {
        background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
        color: white;
        box-shadow: 0 4px 8px rgba(25, 118, 210, 0.3);
        transform: scale(1.05);
    }
}

.menu-item:hover .menu-avatar:not(.menu-avatar-active) {
    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
    color: #1976d2;
    transform: scale(1.05);
}

// Menu Labels
.menu-label {
    font-size: 14px;
    font-weight: 500;
    color: #424242;
    transition: color 0.3s ease;
}

.menu-caption {
    font-size: 11px;
    color: #9e9e9e;
    margin-top: 2px;
    transition: color 0.3s ease;
}

.menu-item:hover .menu-label {
    color: #1976d2;
}

.menu-item:hover .menu-caption {
    color: #1565c0;
}

// Menu Footer
.menu-footer {
    padding: 8px 20px;
    margin-top: auto;
    transition: all 0.3s ease;
}

// Toggle Mini Button
.toggle-mini-container {
    .toggle-mini-btn {
        border-radius: 8px;
        padding: 8px;
        background: #f5f5f5;
        transition: all 0.3s ease;

        &:hover {
            background: #e3f2fd;
            transform: translateX(2px);
        }
    }
}

// Animations
@keyframes fadeIn {
    from {
        opacity: 0;
        transform: scale(0.95);
    }

    to {
        opacity: 1;
        transform: scale(1);
    }
}

@keyframes slideInDown {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes slideInLeft {
    from {
        opacity: 0;
        transform: translateX(-20px);
    }

    to {
        opacity: 1;
        transform: translateX(0);
    }
}

// Responsive
@media (max-width: 599px) {
    .menu-header {
        margin: 0 8px 12px 8px;
        padding: 12px 16px;

        .header-title {
            font-size: 14px;
        }

        .header-subtitle {
            font-size: 10px;
        }
    }

    .menu-item {
        min-height: 56px;
    }

    .menu-label {
        font-size: 13px;
    }

    .menu-caption {
        font-size: 10px;
    }
}
</style>
